import React from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Divider,
  Button,
  Stack,
  Container,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SpaIcon from "@mui/icons-material/Spa";
import SanitizerIcon from "@mui/icons-material/Sanitizer";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HealingIcon from "@mui/icons-material/Healing";
import ForumIcon from "@mui/icons-material/Forum";
import VideocamIcon from "@mui/icons-material/Videocam";
import LinkIcon from "@mui/icons-material/Link";
import * as amplitude from "@amplitude/analytics-browser";
import { Helmet } from "react-helmet-async";

function SalicylicAcidTreatment() {
  return (
    <>
      <Helmet>
        <title>60-Day Plantar Wart Treatment Protocol | The Wart Guy</title>
        <meta
          name="description"
          content="A step-by-step guide to treating plantar warts at home with salicylic acid and tea tree oil. See how I eliminated my warts after 6 years of failed medical treatments."
        />
        <meta
          property="og:title"
          content="60-Day Plantar Wart Treatment Protocol | The Wart Guy"
        />
        <meta
          property="og:description"
          content="A proven home treatment for plantar warts using salicylic acid and tea tree oil."
        />
      </Helmet>

      {/* Header Section */}
      <Box
        sx={{
          background: "linear-gradient(160deg, #f0f9fa 0%, #e0f2f7 100%)",
          pt: { xs: 4, sm: 6, md: 8 },
          pb: { xs: 4, sm: 6, md: 8 },
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              textAlign: "center",
              maxWidth: "800px",
              mx: "auto",
              px: { xs: 2, sm: 3 },
            }}
          >
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 700,
                mb: { xs: 1.5, md: 2 },
              }}
            >
              The 60-Day Plantar Wart Solution
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{
                color: "text.secondary",
                mb: { xs: 3, md: 4 },
                fontSize: { xs: "1rem", sm: "1.1rem", md: "1.3rem" },
              }}
            >
              How I finally eliminated my stubborn plantar warts after 6 years
              of failed medical treatments
            </Typography>

            <Card
              elevation={2}
              sx={{
                p: { xs: 2, sm: 3 },
                mb: { xs: 3, md: 4 },
                backgroundColor: "background.paper",
                borderRadius: 3,
                maxWidth: { xs: "100%", sm: "600px" },
                mx: "auto",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: { xs: 1, sm: 2 },
                  color: "secondary.main",
                }}
              >
                My Frustrating Journey:
              </Typography>
              <List dense sx={{ pt: 0 }}>
                <ListItem sx={{ px: { xs: 0, sm: 2 } }}>
                  <ListItemIcon sx={{ minWidth: { xs: 32, sm: 36 } }}>
                    <CheckCircleOutlineIcon color="error" />
                  </ListItemIcon>
                  <ListItemText primary="Spent over $3,000 on medical treatments" />
                </ListItem>
                <ListItem sx={{ px: { xs: 0, sm: 2 } }}>
                  <ListItemIcon sx={{ minWidth: { xs: 32, sm: 36 } }}>
                    <CheckCircleOutlineIcon color="error" />
                  </ListItemIcon>
                  <ListItemText primary="Consulted 4 different podiatrists" />
                </ListItem>
                <ListItem sx={{ px: { xs: 0, sm: 2 } }}>
                  <ListItemIcon sx={{ minWidth: { xs: 32, sm: 36 } }}>
                    <CheckCircleOutlineIcon color="error" />
                  </ListItemIcon>
                  <ListItemText primary="None of the professional treatments worked" />
                </ListItem>
                <ListItem sx={{ px: { xs: 0, sm: 2 } }}>
                  <ListItemIcon sx={{ minWidth: { xs: 32, sm: 36 } }}>
                    <CheckCircleOutlineIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Finally developed my own solution that worked in 60 days"
                    primaryTypographyProps={{ fontWeight: "bold" }}
                  />
                </ListItem>
              </List>
            </Card>
          </Box>
        </Container>
      </Box>

      <Container
        maxWidth="lg"
        sx={{ py: { xs: 4, sm: 5, md: 6 }, px: { xs: 2, sm: 3, md: 3 } }}
      >
        <Typography
          variant="h3"
          component="h2"
          sx={{
            fontWeight: 600,
            mb: { xs: 3, md: 4 },
            color: "text.primary",
            textAlign: "center",
            fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.5rem" },
          }}
        >
          My Step-by-Step Treatment Protocol
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={10} lg={8} sx={{ mx: "auto" }}>
            {[
              {
                step: 1,
                title: "Debride the Wart",
                description:
                  "Soak your foot in hot water for 10-15 minutes to soften the skin. Using a disposable scalpel or dermal curette, carefully remove as much of the thick callous or skin on top of the wart as possible.",
                note: "This step exposes the wart and allows the treatments to penetrate deeper.",
                color: "primary.main",
              },
              {
                step: 2,
                title: "Apply Salicylic Acid",
                description:
                  'Apply a 40% Salicylic Acid solution (I used "The Wart Stick") directly on the wart. Cover with Tegaderm from Nexcare, which is a transparent bandage that seals in the treatment.',
                note: "The acid works to break down the infected tissue and stimulate your immune response.",
                color: "secondary.main",
              },
              {
                step: 3,
                title: "Wait and Debride Again",
                description:
                  "Wait 12 hours for the acid to work. The upper layer of the wart should turn white. Remove the Tegaderm and debride as much dead skin as possible.",
                important:
                  "Use this opportunity to remove any black spots you see. These are blood vessels that feed the wart. Until ALL black specks are gone, your wart will continue to grow.",
                note: "This step may be bloody. Be patient and persistent, as it will take multiple sessions to remove all the spots.",
                color: "success.main",
              },
              {
                step: 4,
                title: "Apply Tea Tree Oil",
                description:
                  "Apply Tea Tree Oil to a cotton pad and place it on the wart overnight. Cover with Tegaderm to keep it secure.",
                note: "Tea Tree Oil helps disinfect and heal the wart from the stress caused by the Salicylic Acid. You'll notice the white areas of skin begin to disappear.",
                color: "primary.main",
              },
              {
                step: 5,
                title: "Debride and Repeat",
                description:
                  "Remove the Tea Tree Oil the next day. Debride using an electric nail file. Then apply Salicylic Acid again and repeat this process until your wart disappears.",
                note: "It took me around 60 days of consistent treatment for my wart to finally disappear.",
                color: "secondary.main",
              },
              {
                step: 6,
                title: "Adjust as Needed",
                description:
                  "Some days you might want to apply Salicylic Acid for 24 hours, followed by Tea Tree Oil for another 24 hours. Use your best judgment based on how your wart is responding.",
                note: "Everyone's body is different, so adjust the timing as needed for your specific situation.",
                color: "primary.main",
              },
            ].map((step, index) => (
              <Card
                key={index}
                elevation={2}
                sx={{
                  mb: { xs: 2.5, md: 4 },
                  borderRadius: 3,
                  overflow: "hidden",
                  borderLeft: {
                    xs: `3px solid ${step.color}`,
                    sm: `5px solid ${step.color}`,
                  },
                  transition: "transform 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 12px 20px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: { xs: 1, sm: 2 },
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: step.color,
                        color: "white",
                        width: { xs: "28px", sm: "36px" },
                        height: { xs: "28px", sm: "36px" },
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: { xs: 1.5, sm: 2 },
                        fontWeight: "bold",
                        fontSize: { xs: "0.9rem", sm: "1rem" },
                      }}
                    >
                      {step.step}
                    </Box>
                    <Typography
                      variant="h5"
                      component="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" },
                      }}
                    >
                      {step.title}
                    </Typography>
                  </Box>
                  <Typography
                    paragraph
                    sx={{
                      ml: { xs: 5, sm: 7 },
                      mb: step.important ? 1.5 : step.note ? 1.5 : 0,
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                  >
                    {step.description}
                  </Typography>

                  {step.important && (
                    <Box
                      sx={{
                        backgroundColor: "#ffebee",
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 2,
                        ml: { xs: 5, sm: 7 },
                        mb: step.note ? 1.5 : 0,
                      }}
                    >
                      <Typography
                        paragraph
                        sx={{
                          mb: 0,
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                        }}
                      >
                        <strong>Important:</strong> {step.important}
                      </Typography>
                    </Box>
                  )}

                  {step.note && (
                    <Typography
                      paragraph
                      sx={{
                        color: "text.secondary",
                        fontStyle: "italic",
                        ml: { xs: 5, sm: 7 },
                        mb: 0,
                        fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                      }}
                    >
                      {step.note}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Grid>
        </Grid>

        <Divider sx={{ my: { xs: 4, md: 6 } }} />

        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              mb: { xs: 2.5, md: 4 },
              fontSize: { xs: "1.5rem", sm: "1.8rem", md: "2rem" },
            }}
          >
            The Products I Used
          </Typography>

          <Grid container spacing={{ xs: 2, sm: 3 }} justifyContent="center">
            <Grid item xs={12} sm={6} md={4}>
              <Card
                elevation={2}
                sx={{
                  height: "100%",
                  backgroundColor: "background.paper",
                  borderRadius: 3,
                  transition: "transform 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 }, textAlign: "center" }}>
                  <SanitizerIcon
                    sx={{
                      fontSize: { xs: 36, sm: 48 },
                      color: "secondary.main",
                      mb: { xs: 1, sm: 2 },
                    }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    40% Salicylic Acid
                  </Typography>
                  <Typography variant="body2">
                    "The Wart Stick" or any 40% Salicylic Acid solution
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card
                elevation={2}
                sx={{
                  height: "100%",
                  backgroundColor: "background.paper",
                  borderRadius: 3,
                  transition: "transform 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 }, textAlign: "center" }}>
                  <SpaIcon
                    sx={{
                      fontSize: { xs: 36, sm: 48 },
                      color: "success.main",
                      mb: { xs: 1, sm: 2 },
                    }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Tea Tree Oil
                  </Typography>
                  <Typography variant="body2">
                    100% Pure Tea Tree Essential Oil
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card
                elevation={2}
                sx={{
                  height: "100%",
                  backgroundColor: "background.paper",
                  borderRadius: 3,
                  transition: "transform 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 }, textAlign: "center" }}>
                  <HealingIcon
                    sx={{
                      fontSize: { xs: 36, sm: 48 },
                      color: "primary.main",
                      mb: { xs: 1, sm: 2 },
                    }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Tegaderm Bandages
                  </Typography>
                  <Typography variant="body2">
                    Nexcare Tegaderm Transparent Dressing
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Button
            variant="contained"
            color="secondary"
            size="large"
            fullWidth={{ xs: true, sm: false }}
            component="a"
            href="https://linktr.ee/thewartguy"
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<LinkIcon />}
            aria-label="View all products on LinkTree"
            sx={{
              px: { xs: 3, sm: 4 },
              py: { xs: 1, sm: 1.5 },
              mt: { xs: 3, md: 5 },
              mb: 1,
              maxWidth: { xs: "100%", sm: "auto" },
              mx: "auto",
            }}
            onClick={() => {
              amplitude.logEvent("View Linktree", {
                location: "treatment_page",
              });
            }}
          >
            View All Products on LinkTree
          </Button>
          <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
            Full list of exact products I used during my treatment
          </Typography>
        </Box>

        <Divider sx={{ my: { xs: 4, md: 6 } }} />

        <Box sx={{ textAlign: "center", mb: { xs: 3, md: 4 } }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: { xs: 2, md: 3 },
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
            }}
          >
            Follow My Journey
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 1.5, sm: 2 }}
            justifyContent="center"
            sx={{ mb: { xs: 3, md: 4 } }}
          >
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth={{ xs: true, sm: false }}
              component="a"
              href="https://www.tiktok.com/@thewartguy?is_from_webapp=1&sender_device=pc"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<VideocamIcon />}
              aria-label="See daily progress on TikTok"
              sx={{
                px: { xs: 3, sm: 4 },
                py: { xs: 1, sm: 1.5 },
              }}
              onClick={() => {
                amplitude.logEvent("View TikTok", {
                  location: "treatment_page",
                });
              }}
            >
              See My Daily Progress on TikTok
            </Button>

            <Button
              variant="outlined"
              color="primary"
              size="large"
              fullWidth={{ xs: true, sm: false }}
              component="a"
              href="https://discord.gg/Fq69xjmDVx"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<ForumIcon />}
              aria-label="Get personalized help on Discord"
              sx={{
                px: { xs: 3, sm: 4 },
                py: { xs: 1, sm: 1.5 },
              }}
              onClick={() => {
                amplitude.logEvent("Join Discord", {
                  location: "treatment_page",
                });
              }}
            >
              Get Personalized Help on Discord
            </Button>
          </Stack>
          <Box sx={{ mt: { xs: 2, md: 4 } }}>
            <Typography
              sx={{ color: "text.secondary", mb: { xs: 0.5, md: 1 } }}
            >
              <strong>TikTok:</strong> Watch my daily treatment updates and
              progress
            </Typography>
            <Typography
              sx={{ color: "text.secondary", mb: { xs: 0.5, md: 1 } }}
            >
              <strong>Discord:</strong> Join our community for personalized
              advice and support
            </Typography>
          </Box>
        </Box>
      </Container>
    </>
  );
}

export default SalicylicAcidTreatment;
